import React, { useEffect, useRef, useState } from "react";
import { Lightbulb, X, ArrowRight } from "lucide-react";

const TIPS = [
  { cat: "Recall", tip: "The most powerful study tool is closing the book and writing down what you just read. If you can't reproduce it from memory, you don't know it yet." },
  { cat: "Recall", tip: "After each lesson, spend 60 seconds explaining it out loud like you're teaching a friend. If you stumble, that's exactly where to review." },
  { cat: "Spacing", tip: "Review new material at 1 day, 3 days, and 7 days. This 'spaced repetition' beats cramming by 200%." },
  { cat: "Spacing", tip: "Short daily sessions crush long weekend cramming. 25 minutes a day × 5 days > 3 hours on Sunday." },
  { cat: "Focus", tip: "The Pomodoro Trick: 25 min focused study + 5 min break. Your brain resets and retention doubles." },
  { cat: "Focus", tip: "Silence your phone and put it in another room. Even a face-down phone in the same room drops recall by 20%." },
  { cat: "Numbers", tip: "For every rule that has a NUMBER (7 years, 180 days, 3 business days), write the trigger next to it. Numbers only stick when attached to WHY they exist." },
  { cat: "Numbers", tip: "The 3-day rule pops up everywhere. When in doubt on a disclosure deadline, guess 3 business days — you'll be right most of the time." },
  { cat: "Acronyms", tip: "For alphabet-soup laws (TILA, RESPA, ECOA, HMDA), memorize the PURPOSE first, then the deadlines. Purpose is easier to reason from." },
  { cat: "Recall", tip: "Cover the answer. Read the question. Say the answer out loud. Then reveal. Passive reading kills memory." },
  { cat: "Application", tip: "The exam tests APPLICATION, not memorization. If you can only recite the rule, you'll miss 40% of questions. Practice on real scenarios." },
  { cat: "Test-Day", tip: "RTFQ×2 — Read The Full Question TWICE. The last sentence usually contains the actual question being asked." },
  { cat: "Test-Day", tip: "Answer every question, even the tough ones. Flag the ones you're unsure of. Momentum > perfection." },
  { cat: "Test-Day", tip: "Save 30 minutes for review. That's when your best score gains happen — not on the first pass." },
  { cat: "Math", tip: "For every mortgage math problem, write down the formula BEFORE plugging in numbers. You'll catch 90% of errors this way." },
  { cat: "Math", tip: "Rounding rule: mortgage math ALWAYS rounds UP to the nearest whole percent. 92.31% LTV → 93%. Always." },
  { cat: "Math", tip: "Bi-weekly = ×26 ÷ 12. Semi-monthly = ×24 ÷ 12. Confusing these two costs you an easy point on the exam." },
  { cat: "Fatigue", tip: "Studying tired = studying nothing. Sleep is when memory consolidates. 7+ hours the night before the exam is non-negotiable." },
  { cat: "Nutrition", tip: "Water. Every 90 minutes of study, drink a glass. Dehydration drops cognition by 12%." },
  { cat: "Confidence", tip: "You'll fail practice questions and that's fine. Every missed question is a diagnostic pointing to what to study next. Miss it now, not at Prometric." },
  { cat: "Confidence", tip: "Diane's rule: if you're getting 85%+ on practice exams, you're ready. Book the test." },
  { cat: "Recall", tip: "Interleave your practice — mix Federal Law questions with SAFE Act and math. Same-category batching feels smoother but leaks in 2 days." },
  { cat: "Recall", tip: "Sketch a one-page mind map for each module. The act of drawing forces you to organize knowledge into structure." },
  { cat: "Recall", tip: "For dates and thresholds, invent stories. \"78% is when PMI has to be booted — think of it as PMI's forced retirement.\" Silly = sticky." },
  { cat: "Test-Day", tip: "Beware absolutes: answers with 'always,' 'never,' or 'only' are usually wrong. Regulatory law loves exceptions." },
  { cat: "Test-Day", tip: "If two answers seem right, one is a distractor. Look for the one that FULLY covers the question — not just partially." },
  { cat: "Habit", tip: "Same time, same place. Your brain associates location with focus. Study in your bed = study nap." },
  { cat: "Habit", tip: "The 2-Minute Rule: if a topic feels overwhelming, start with just 2 minutes. Momentum is everything — you'll usually keep going." },
];

const IDLE_MS = 60_000;         // show first tip after 60s of tab activity
const REPEAT_MS = 4 * 60_000;   // then repeat every 4 minutes
const DISMISS_MS = 10 * 60_000; // after "Got it" hide for 10 min
const STORAGE_KEY = "dw_tip_last_shown_at";
const STORAGE_INDEX = "dw_tip_index";
const STORAGE_DISABLED = "dw_tips_disabled";

export default function LearningTipPopup() {
  const [tip, setTip] = useState(null);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  function pickTip() {
    const idxStr = sessionStorage.getItem(STORAGE_INDEX);
    let idx = idxStr ? parseInt(idxStr, 10) : Math.floor(Math.random() * TIPS.length);
    idx = (idx + 1) % TIPS.length;
    sessionStorage.setItem(STORAGE_INDEX, String(idx));
    return TIPS[idx];
  }

  function show() {
    if (localStorage.getItem(STORAGE_DISABLED) === "1") return;
    setTip(pickTip());
    setVisible(true);
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  }

  function schedule(delay) {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (document.visibilityState === "visible") {
        show();
      } else {
        // Reschedule when tab becomes visible again
        schedule(delay);
      }
    }, delay);
  }

  useEffect(() => {
    // First appearance
    const last = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    const sinceLast = Date.now() - last;
    const initial = last === 0 ? IDLE_MS : Math.max(IDLE_MS, REPEAT_MS - sinceLast);
    schedule(initial);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, []);

  function dismiss() {
    setVisible(false);
    // Reschedule next appearance
    schedule(REPEAT_MS);
  }

  function gotIt() {
    setVisible(false);
    // Longer cooldown after acknowledgement
    schedule(DISMISS_MS);
  }

  function disableForever() {
    localStorage.setItem(STORAGE_DISABLED, "1");
    setVisible(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }

  if (!visible || !tip) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-40 max-w-sm dw-fade-up pointer-events-auto"
      data-testid="learning-tip-popup"
    >
      <div className="relative bg-[var(--dw-parchment)] border border-[var(--dw-navy)]/25 rounded-sm shadow-2xl overflow-hidden">
        {/* Gold accent bar */}
        <div className="absolute inset-y-0 left-0 w-1 bg-[var(--dw-gold)]" />

        <div className="pl-5 pr-4 py-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 shrink-0 rounded-sm border border-[var(--dw-gold)]/60 bg-[var(--dw-gold)]/10 flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-[var(--dw-gold-soft)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <div className="overline text-[10px] text-[var(--dw-gold-soft)]" data-testid="tip-category">
                  Study Tip · {tip.cat}
                </div>
                <button
                  onClick={dismiss}
                  data-testid="tip-close"
                  className="text-neutral-400 hover:text-[var(--dw-navy)] -mr-1"
                  aria-label="Close tip"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="text-sm text-[var(--dw-navy)] leading-relaxed mb-3" data-testid="tip-text">
                {tip.tip}
              </div>
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={disableForever}
                  data-testid="tip-disable"
                  className="text-[11px] text-neutral-500 hover:text-neutral-800 underline underline-offset-2"
                >
                  Stop showing tips
                </button>
                <button
                  onClick={gotIt}
                  data-testid="tip-gotit"
                  className="text-xs text-[var(--dw-navy)] font-medium hover:text-[var(--dw-gold-soft)] inline-flex items-center gap-1"
                >
                  Got it <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
